import clsx from 'clsx';
import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'antd';
import { useRequestLeave } from '../../../hooks/useRequestLeave';
import { Icon } from '../Icon';

interface RightPanelProps {
  checkDirtyOnClose?: boolean;
  isOpen: boolean;
  onClose: () => void;
  children?: ReactNode;
  hasCloseButton?: boolean;
  isHeaderVisible?: boolean;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Global list-mode details/chat panel. When FilegrantViewerModal is open, MainLayout
 * keeps this closed — Details / AI views render as a nested split inside the modal.
 */
const RightPanel: React.FC<RightPanelProps> = ({
  checkDirtyOnClose = true,
  isOpen,
  onClose,
  children,
  hasCloseButton = true,
  isHeaderVisible = true,
}) => {
  const { t } = useTranslation();
  const requestLeave = useRequestLeave();
  const asideRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  const handleClose = async () => {
    if (checkDirtyOnClose) {
      const canLeave = await requestLeave();
      if (!canLeave) {
        return;
      }
    }

    onClose();
    triggerElementRef.current?.focus();
  };

  const animationDuration = 100;

  const [isContentVisible, setIsContentVisible] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (isOpen) {
      triggerElementRef.current = document.activeElement as HTMLElement | null;
      timeout = setTimeout(() => {
        setIsContentVisible(true);
        closeButtonRef.current?.focus();
      }, animationDuration + 100);
    } else {
      setIsContentVisible(false);
    }

    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !asideRef.current) {
        return;
      }

      const focusableElements = Array.from(
        asideRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => element.offsetParent !== null);

      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isContentVisible]);

  return (
    <aside
      ref={asideRef}
      aria-label={t('a11y.detailsPanel')}
      aria-hidden={!isOpen}
      className={clsx(
        `border-gray-medium fixed right-0 bottom-0 z-30 flex shrink-0 flex-col rounded-tl-md rounded-bl-md border bg-white transition-all duration-${animationDuration} overflow-hidden ease-in-out`,
        isOpen ? 'w-full max-w-[480px] opacity-100' : 'pointer-events-none w-0 max-w-0 opacity-0',
        isHeaderVisible ? 'top-[var(--app-header-stack-height,3.5rem)]' : 'top-0',
      )}
    >
      {hasCloseButton && (
        <div className="absolute top-1 right-1 z-40 flex shrink-0 items-center">
          <Button
            ref={closeButtonRef}
            aria-label={t('a11y.closePanel')}
            type="text"
            onClick={() => {
              void handleClose();
            }}
          >
            <Icon
              name="XIcon"
              className={clsx(
                'm-auto transition-all duration-200 ease-in-out',
                isOpen ? 'rotate-180' : 'rotate-0',
              )}
            />
          </Button>
        </div>
      )}
      {isContentVisible && (
        <div className={clsx('relative h-full px-2', hasCloseButton && 'pt-8')}>{children}</div>
      )}
    </aside>
  );
};

export default RightPanel;
